import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import AppLayout from '../../components/AppLayout';
import { S, Icon, Icons, BtnPrimary, BtnSecondary, LOCALE_CONFIG } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── Module Definitions (labels/descs resolved per-render via i18n) ──
const MODULES = [
  { key: 'claims', tName: 'screens.onboardOrg.modClaims', tDesc: 'screens.onboardOrg.modClaimsDesc', icon: 'file-text', core: true },
  { key: 'policies', tName: 'screens.onboardOrg.modPolicies', tDesc: 'screens.onboardOrg.modPoliciesDesc', icon: 'shield', core: true },
  { key: 'documentVault', tName: 'screens.onboardOrg.modVault', tDesc: 'screens.onboardOrg.modVaultDesc', icon: 'folder', core: true },
  { key: 'invoicing', tName: 'screens.onboardOrg.modInvoicing', tDesc: 'screens.onboardOrg.modInvoicingDesc', icon: 'receipt' },
  { key: 'commissions', tName: 'screens.onboardOrg.modCommissions', tDesc: 'screens.onboardOrg.modCommissionsDesc', icon: 'percent' },
  { key: 'brokers', tName: 'screens.onboardOrg.modBrokers', tDesc: 'screens.onboardOrg.modBrokersDesc', icon: 'briefcase' },
  { key: 'riskCarriers', tName: 'screens.onboardOrg.modRiskCarriers', tDesc: 'screens.onboardOrg.modRiskCarriersDesc', icon: 'building' },
  { key: 'compliance', tName: 'screens.onboardOrg.modCompliance', tDesc: 'screens.onboardOrg.modComplianceDesc', icon: 'check-circle' },
  { key: 'eSignatures', tName: 'screens.onboardOrg.modESignatures', tDesc: 'screens.onboardOrg.modESignaturesDesc', icon: 'pen-tool' },
  { key: 'aiAssistant', tName: 'screens.onboardOrg.modAI', tDesc: 'screens.onboardOrg.modAIDesc', icon: 'activity' },
  { key: 'bordereaux', tName: 'screens.onboardOrg.modBordereaux', tDesc: 'screens.onboardOrg.modBordereauxDesc', icon: 'bar-chart' },
  { key: 'riskSignals', tName: 'screens.onboardOrg.modRiskSignals', tDesc: 'screens.onboardOrg.modRiskSignalsDesc', icon: 'alert-triangle' },
  { key: 'messaging', tName: 'screens.onboardOrg.modMessaging', tDesc: 'screens.onboardOrg.modMessagingDesc', icon: 'message-circle' },
  { key: 'videoConference', tName: 'screens.onboardOrg.modVideo', tDesc: 'screens.onboardOrg.modVideoDesc', icon: 'video' },
];

const INDUSTRIES = ['General Insurance', 'Health', 'Property & Casualty', 'Life', 'Reinsurance'];
const COUNTRIES = ['United States', 'United Kingdom', 'Switzerland', 'Germany', 'France', 'United Arab Emirates', 'Canada', 'Australia', 'India', 'Singapore'];
const TIMEZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London', 'Europe/Zurich', 'Europe/Berlin', 'Europe/Paris', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Kolkata', 'Australia/Sydney'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'AED', 'CAD', 'AUD', 'INR', 'SGD'];

// ─── Input Field Component ───────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 14px', background: '#fff',
  border: `1px solid ${S.borderLight}`, borderRadius: 8,
  fontSize: 13, color: S.textPrimary, outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: S.textSecondary, marginBottom: 6, display: 'block' };

const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: S.danger }}> *</span>}</label>
    {children}
  </div>
);

// ─── Toggle Switch ───────────────────────────────────────
const Toggle = ({ checked, onChange, disabled }) => (
  <div
    onClick={disabled ? undefined : onChange}
    style={{
      width: 44, height: 24, borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer',
      background: disabled ? '#e2e8f0' : checked ? S.burgundy : '#cbd5e1',
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <div style={{
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 3, left: checked ? 23 : 3,
      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </div>
);

// ─── Generate Secure Password ────────────────────────────
const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*';
  let pw = '';
  for (let i = 0; i < 16; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw;
};

// ═════════════════════════════════════════════════════════════
const OnboardOrgScreen = () => {
  const { t } = useTranslation();
  const STEP_LABELS = [
    t('screens.onboardOrg.stepOrg'),
    t('screens.onboardOrg.stepModules'),
    t('screens.onboardOrg.stepAdmin'),
    t('screens.onboardOrg.stepQuotas'),
    t('screens.onboardOrg.stepReview'),
  ];
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Step 1: Org details
  const [orgName, setOrgName] = useState('');
  const [orgEmail, setOrgEmail] = useState('');
  const [orgPhone, setOrgPhone] = useState('');
  const [orgAddress, setOrgAddress] = useState('');
  const [industry, setIndustry] = useState('');
  const [country, setCountry] = useState('');
  const [timezone, setTimezone] = useState('');
  const [currency, setCurrency] = useState(LOCALE_CONFIG.currency);

  // Step 2: Modules
  const [enabledModules, setEnabledModules] = useState(() => {
    const m = {};
    MODULES.forEach(mod => { m[mod.key] = mod.core ? true : false; });
    return m;
  });

  // Step 3: Admin user
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  // Step 4: Quotas
  const [maxUsers, setMaxUsers] = useState(50);
  const [maxStorageGB, setMaxStorageGB] = useState(10);
  const [apiRateLimit, setApiRateLimit] = useState(1000);

  const toggleModule = (key) => {
    const mod = MODULES.find(m => m.key === key);
    if (mod?.core) return;
    setEnabledModules(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ─── Validation ────────────────────────────────────────
  const validateStep = () => {
    setError('');
    if (step === 0) {
      if (!orgName.trim()) { setError(t('screens.onboardOrg.errOrgName')); return false; }
      if (!orgEmail.trim()) { setError(t('screens.onboardOrg.errEmail')); return false; }
      if (!orgPhone.trim()) { setError(t('screens.onboardOrg.errPhone')); return false; }
    }
    if (step === 2) {
      if (!adminName.trim()) { setError(t('screens.onboardOrg.errAdminName')); return false; }
      if (!adminEmail.trim()) { setError(t('screens.onboardOrg.errAdminEmail')); return false; }
      if (!adminPassword.trim()) { setError(t('screens.onboardOrg.errPassword')); return false; }
      if (adminPassword.length < 8) { setError(t('screens.onboardOrg.errPasswordLen')); return false; }
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStep(s => Math.min(s + 1, 4));
  };
  const goBack = () => { setError(''); setStep(s => Math.max(s - 1, 0)); };

  // ─── Submit ────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const selectedModules = {};
      Object.entries(enabledModules).forEach(([k, v]) => { selectedModules[k] = v; });

      const body = {
        org: {
          name: orgName.trim(),
          email: orgEmail.trim(),
          phone: orgPhone.trim(),
          address: orgAddress.trim(),
          industry,
          country,
          timezone,
          currency,
          modules: selectedModules,
          quotas: { maxUsers, maxStorageGB, apiRateLimit },
        },
        admin: {
          name: adminName.trim(),
          email: adminEmail.trim(),
          phone: adminPhone.trim(),
          password: adminPassword,
        },
      };

      const resp = await axios.post(`${BASE_URL}/orgs/onboard`, body, { withCredentials: true });
      const newOrgId = resp.data?.data?._id || resp.data?.data?.org?._id;
      if (newOrgId) {
        history.push(`/orgs/${newOrgId}`);
      } else {
        history.push('/platform');
      }
    } catch (err) {
      setError(err.response?.data?.message || t('screens.onboardOrg.errFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(adminPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ─── Step Indicator ────────────────────────────────────
  const StepIndicator = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 32 }}>
      {STEP_LABELS.map((label, i) => (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: i < step ? S.burgundy : i === step ? S.burgundy : '#e2e8f0',
              color: i <= step ? '#fff' : S.textMuted,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, transition: 'all 0.3s',
            }}>
              {i < step ? <Icon name="check" color="#fff" size={16} /> : i + 1}
            </div>
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: i <= step ? S.burgundy : S.textMuted,
            }}>{label}</span>
          </div>
          {i < STEP_LABELS.length - 1 && (
            <div style={{
              width: 60, height: 2, background: i < step ? S.burgundy : '#e2e8f0',
              marginBottom: 20, transition: 'background 0.3s',
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ─── Step 1: Organization Details ──────────────────────
  const renderStep1 = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.onboardOrg.orgDetailsTitle')}</h3>
      <p style={{ fontSize: 13, color: S.textMuted, marginBottom: 24 }}>{t('screens.onboardOrg.orgDetailsBlurb')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
        <Field label={t('screens.onboardOrg.orgName')} required>
          <input style={inputStyle} value={orgName} onChange={e => setOrgName(e.target.value)} placeholder={t('screens.onboardOrg.orgNamePh')} />
        </Field>
        <Field label={t('screens.onboardOrg.email')} required>
          <input style={inputStyle} type="email" value={orgEmail} onChange={e => setOrgEmail(e.target.value)} placeholder={t('screens.onboardOrg.emailPh')} />
        </Field>
        <Field label={t('screens.onboardOrg.phone')} required>
          <input style={inputStyle} value={orgPhone} onChange={e => setOrgPhone(e.target.value)} placeholder={t('screens.onboardOrg.phonePh')} />
        </Field>
        <Field label={t('screens.onboardOrg.address')}>
          <input style={inputStyle} value={orgAddress} onChange={e => setOrgAddress(e.target.value)} placeholder={t('screens.onboardOrg.addressPh')} />
        </Field>
        <Field label={t('screens.onboardOrg.industry')}>
          <select style={inputStyle} value={industry} onChange={e => setIndustry(e.target.value)}>
            <option value="">{t('screens.onboardOrg.selectIndustry')}</option>
            {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
          </select>
        </Field>
        <Field label={t('screens.onboardOrg.country')}>
          <select style={inputStyle} value={country} onChange={e => setCountry(e.target.value)}>
            <option value="">{t('screens.onboardOrg.selectCountry')}</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label={t('screens.onboardOrg.timezone')}>
          <select style={inputStyle} value={timezone} onChange={e => setTimezone(e.target.value)}>
            <option value="">{t('screens.onboardOrg.selectTimezone')}</option>
            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </Field>
        <Field label={t('screens.onboardOrg.currency')}>
          <select style={inputStyle} value={currency} onChange={e => setCurrency(e.target.value)}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );

  // ─── Step 2: Module Selection ──────────────────────────
  const renderStep2 = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.onboardOrg.moduleTitle')}</h3>
      <p style={{ fontSize: 13, color: S.textMuted, marginBottom: 24 }}>{t('screens.onboardOrg.moduleBlurb')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {MODULES.map(mod => {
          const enabled = enabledModules[mod.key];
          return (
            <div key={mod.key} style={{
              background: '#fff', border: `1px solid ${enabled ? S.burgundy + '40' : S.borderLight}`,
              borderRadius: 12, padding: '20px 18px',
              opacity: mod.core ? 0.85 : 1,
              transition: 'border-color 0.2s',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: enabled ? S.burgundy + '14' : '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name={mod.icon} color={enabled ? S.burgundy : S.textMuted} size={20} />
                </div>
                <Toggle checked={enabled} onChange={() => toggleModule(mod.key)} disabled={mod.core} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary, marginBottom: 4 }}>{t(mod.tName)}</div>
              <div style={{ fontSize: 12, color: S.textMuted }}>{t(mod.tDesc)}</div>
              {mod.core && (
                <div style={{ fontSize: 10, fontWeight: 700, color: S.burgundy, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 8 }}>{t('screens.onboardOrg.coreModule')}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─── Step 3: First Admin User ──────────────────────────
  const renderStep3 = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.onboardOrg.adminTitle')}</h3>
      <p style={{ fontSize: 13, color: S.textMuted, marginBottom: 24 }}>{t('screens.onboardOrg.adminBlurb')}</p>
      <div style={{ maxWidth: 500 }}>
        <Field label={t('screens.onboardOrg.fullName')} required>
          <input style={inputStyle} value={adminName} onChange={e => setAdminName(e.target.value)} placeholder={t('screens.onboardOrg.fullNamePh')} />
        </Field>
        <Field label={t('screens.onboardOrg.email')} required>
          <input style={inputStyle} type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder={t('screens.onboardOrg.adminEmailPh')} />
        </Field>
        <Field label={t('screens.onboardOrg.phone')}>
          <input style={inputStyle} value={adminPhone} onChange={e => setAdminPhone(e.target.value)} placeholder={t('screens.onboardOrg.adminPhonePh')} />
        </Field>
        <Field label={t('screens.onboardOrg.password')} required>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }}
              type="text"
              value={adminPassword}
              onChange={e => setAdminPassword(e.target.value)}
              placeholder={t('screens.onboardOrg.passwordPh')}
            />
            <button
              onClick={() => setAdminPassword(generatePassword())}
              style={{
                padding: '10px 16px', background: S.bgCard, border: `1px solid ${S.borderLight}`,
                borderRadius: 8, fontSize: 12, fontWeight: 600, color: S.textSecondary,
                cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <Icon name="refresh" color={S.textMuted} size={14} />
              {t('screens.onboardOrg.autoGenerate')}
            </button>
            {adminPassword && (
              <button
                onClick={copyPassword}
                style={{
                  padding: '10px 14px', background: copied ? '#dcfce7' : S.bgCard,
                  border: `1px solid ${copied ? '#86efac' : S.borderLight}`,
                  borderRadius: 8, fontSize: 12, fontWeight: 600,
                  color: copied ? '#15803d' : S.textSecondary,
                  cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Icon name={copied ? 'check' : 'link'} color={copied ? '#15803d' : S.textMuted} size={14} />
                {copied ? t('screens.onboardOrg.copied') : t('screens.onboardOrg.copy')}
              </button>
            )}
          </div>
        </Field>
        <div style={{ padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="alert-circle" color="#1d4ed8" size={14} />
            {t('screens.onboardOrg.welcomeEmailNote')}
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Step 4: Quotas ────────────────────────────────────
  const renderStep4 = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.onboardOrg.quotasTitle')}</h3>
      <p style={{ fontSize: 13, color: S.textMuted, marginBottom: 24 }}>{t('screens.onboardOrg.quotasBlurb')}</p>
      <div style={{ maxWidth: 500 }}>
        <Field label={t('screens.onboardOrg.maxUsers')}>
          <input style={inputStyle} type="number" min={1} value={maxUsers} onChange={e => setMaxUsers(Number(e.target.value) || 0)} />
          <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>{t('screens.onboardOrg.maxUsersHint')}</div>
        </Field>
        <Field label={t('screens.onboardOrg.maxStorage')}>
          <input style={inputStyle} type="number" min={1} value={maxStorageGB} onChange={e => setMaxStorageGB(Number(e.target.value) || 0)} />
          <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>{t('screens.onboardOrg.maxStorageHint')}</div>
        </Field>
        <Field label={t('screens.onboardOrg.apiRate')}>
          <input style={inputStyle} type="number" min={100} value={apiRateLimit} onChange={e => setApiRateLimit(Number(e.target.value) || 0)} />
          <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>{t('screens.onboardOrg.apiRateHint')}</div>
        </Field>
      </div>
    </div>
  );

  // ─── Step 5: Review ────────────────────────────────────
  const renderStep5 = () => {
    const selectedModules = MODULES.filter(m => enabledModules[m.key]);
    return (
      <div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.onboardOrg.reviewTitle')}</h3>
        <p style={{ fontSize: 13, color: S.textMuted, marginBottom: 24 }}>{t('screens.onboardOrg.reviewBlurb')}</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Organization Details */}
          <div style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="building" color={S.burgundy} size={16} /> {t('screens.onboardOrg.reviewOrgDetails')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <ReviewRow label={t('screens.onboardOrg.labelName')} value={orgName} />
              <ReviewRow label={t('screens.onboardOrg.labelEmail')} value={orgEmail} />
              <ReviewRow label={t('screens.onboardOrg.labelPhone')} value={orgPhone} />
              {orgAddress && <ReviewRow label={t('screens.onboardOrg.labelAddress')} value={orgAddress} />}
              {industry && <ReviewRow label={t('screens.onboardOrg.labelIndustry')} value={industry} />}
              {country && <ReviewRow label={t('screens.onboardOrg.labelCountry')} value={country} />}
              {timezone && <ReviewRow label={t('screens.onboardOrg.labelTimezone')} value={timezone} />}
              <ReviewRow label={t('screens.onboardOrg.labelCurrency')} value={currency} />
            </div>
          </div>

          {/* Admin User */}
          <div style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="user" color={S.burgundy} size={16} /> {t('screens.onboardOrg.reviewAdminUser')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <ReviewRow label={t('screens.onboardOrg.labelName')} value={adminName} />
              <ReviewRow label={t('screens.onboardOrg.labelEmail')} value={adminEmail} />
              {adminPhone && <ReviewRow label={t('screens.onboardOrg.labelPhone')} value={adminPhone} />}
              <ReviewRow label={t('screens.onboardOrg.labelPassword')} value={'*'.repeat(adminPassword.length)} />
            </div>
          </div>

          {/* Modules */}
          <div style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="grid" color={S.burgundy} size={16} /> {t('screens.onboardOrg.reviewEnabledModules', { count: selectedModules.length })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {selectedModules.map(mod => (
                <span key={mod.key} style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: mod.core ? S.burgundy + '14' : '#dbeafe',
                  color: mod.core ? S.burgundy : '#1d4ed8',
                }}>
                  {t(mod.tName)}
                </span>
              ))}
            </div>
          </div>

          {/* Quotas */}
          <div style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="bar-chart" color={S.burgundy} size={16} /> {t('screens.onboardOrg.reviewQuotas')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <ReviewRow label={t('screens.onboardOrg.labelMaxUsers')} value={maxUsers} />
              <ReviewRow label={t('screens.onboardOrg.labelMaxStorage')} value={t('screens.onboardOrg.gbSuffix', { n: maxStorageGB })} />
              <ReviewRow label={t('screens.onboardOrg.labelApiRate')} value={t('screens.onboardOrg.reqPerMin', { n: apiRateLimit })} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ReviewRow = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: S.textMuted }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{value || '—'}</span>
    </div>
  );

  const STEPS = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>

        {/* Header */}
        <header style={{
          padding: '20px 32px', borderBottom: `1px solid ${S.borderLight}`,
          background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => history.push('/platform')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
            >
              <Icon name="arrowLeft" color={S.textMuted} size={20} />
            </button>
            <div>
              <div style={{ fontSize: 12, color: S.textMuted, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                {t('screens.onboardOrg.breadcrumbPlatform')}
                <Icon name="chevronRight" color={S.textMuted} size={12} />
                <span style={{ color: S.textPrimary, fontWeight: 500 }}>{t('screens.onboardOrg.breadcrumbOnboard')}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary }}>{t('screens.onboardOrg.title')}</div>
            </div>
          </div>
        </header>

        <div style={{ flex: 1, padding: '32px 32px', maxWidth: 960, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

          <StepIndicator />

          {/* Error banner */}
          {error && (
            <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="alert-circle" color="#dc2626" size={16} />
              <span style={{ fontSize: 13, color: '#991b1b' }}>{error}</span>
            </div>
          )}

          {/* Step Content */}
          {STEPS[step]()}

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, paddingTop: 24, borderTop: `1px solid ${S.borderLight}` }}>
            <div>
              {step > 0 && (
                <BtnSecondary onClick={goBack}>
                  <Icon name="arrowLeft" color={S.textSecondary} size={16} />
                  {t('screens.onboardOrg.back')}
                </BtnSecondary>
              )}
            </div>
            <div>
              {step < 4 ? (
                <BtnPrimary onClick={goNext}>
                  {t('screens.onboardOrg.next')}
                  <Icon name="chevronRight" color="#fff" size={14} />
                </BtnPrimary>
              ) : (
                <BtnPrimary onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                      <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      {t('screens.onboardOrg.creating')}
                    </>
                  ) : (
                    <>
                      <Icon name="check" color="#fff" size={16} />
                      {t('screens.onboardOrg.createOrg')}
                    </>
                  )}
                </BtnPrimary>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default OnboardOrgScreen;
